import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMembersQuery } from '@platform/state/api';
import { routes } from '@platform/core/lib/routes';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { useWorkspaceMeta } from '../../shell/useWorkspaceMeta';
import { BiBarsCard } from './BiBarsCard';
import { BiKpis } from './BiKpis';
import { BiModulesTable } from './BiModulesTable';
import type { BiView } from './BiView';
import { BiWeeksChart } from './BiWeeksChart';
import { useBiStats } from './useBiStats';
import './bi.css';

const VIEWS: readonly BiView[] = ['overview', 'workload', 'trends'];

/**
 * Бізнес-аналітика робочого простору (прототип v4 page "bi", Головна › Бізнес-аналітика): «Огляд робочого простору» / «Навантаження» /
 * «Тенденції», опис «Бізнес-аналітика для простору — усі модулі разом.», показники; огляд — таблиця модулів, навантаження — відкриті й
 * прострочені за виконавцем і відкриті за модулем, тенденції — створено й завершено за 8 тижнів і найактивніші модулі за 30 днів.
 */
export function BiPage() {
  const { t } = useTranslation();
  const { view } = useParams();
  const { ws } = useWorkspaceParams();
  const { workspace } = useWorkspaceMeta(ws);
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const stats = useBiStats(ws);
  if (!VIEWS.includes(view as BiView)) return <Navigate to={routes.home()} replace />;
  return (
    <div className="bi-page">
      <div className="bi-head">
        <h1>{t(`biPage.${view}`)}</h1>
        <span className="muted">{t('biPage.sub', { ws: workspace?.name ?? '' })}</span>
      </div>
      <BiKpis stats={stats} modules={stats?.modules.length ?? 0} members={members?.length} />
      {!stats && <div className="bi-loading">{t('homeViews.loading')}</div>}
      {stats && view === 'overview' && <BiModulesTable rows={stats.modules} />}
      {stats && view === 'workload' && (
        <div className="bi-grid">
          <BiBarsCard title={t('biPage.openByAssignee')} bars={stats.people} color="var(--bi-purple)" />
          <BiBarsCard title={t('biPage.overdueByAssignee')} bars={stats.peopleOverdue} color="var(--bi-orange)" />
          <BiBarsCard title={t('biPage.openByModule')} bars={stats.moduleOpen} color="var(--bi-blue)" />
        </div>
      )}
      {stats && view === 'trends' && (
        <>
          <BiWeeksChart weeks={stats.weeks} />
          <BiBarsCard title={t('biPage.active')} bars={stats.active} color="var(--bi-green)" />
        </>
      )}
    </div>
  );
}
